import { useMemo } from 'react';
import { usePermissions } from '@/features/auth';
import { useCustomerQuery } from '@/features/customers/hooks/useCustomerQuery';
import { resolveHolder } from '../utils/holderResolution';
import type { Agreement } from '../types/agreements.types';
import { isServiceableStatus } from '../utils/agreementStatus';

export type HolderSource = 'pending' | 'current' | 'snapshot';

export function useAgreementHolder(agreement: Agreement | undefined) {
    const { hasPermission } = usePermissions();

    const canReadCustomer = hasPermission({ resource: 'customer', action: 'read' });
    const customerId = agreement?.customerId || undefined;

    // Gated on the permission as well as the id, so a caller who would certainly
    // be refused never spends the request. The 403 fallback is the boundary.
    const live = useCustomerQuery(canReadCustomer && customerId ? customerId : undefined);

    const holder = useMemo(
        () => resolveHolder(agreement?.customerSnapshot, live.data),
        [agreement?.customerSnapshot, live.data],
    );

    // Three states, not two. 'snapshot' is a CLAIM about which record is on
    // screen and must not be made while the answer is still in flight.
    const source: HolderSource = live.data
        ? 'current'
        : live.isPending && canReadCustomer && !!customerId
          ? 'pending'
          : 'snapshot';

    const isAmendable = agreement !== undefined && isServiceableStatus(agreement.status);

    // The form seeds from the live record, so there must be one to seed from.
    const affordable = isAmendable && source === 'current';

    const canEditCustomer = hasPermission({ resource: 'customer', action: 'edit' });

    return {
        customerId,
        holder,
        source,
        liveCustomer: live.data,
        canChangeContactDetails: affordable && canEditCustomer,
        // Needs both. Internal roles only: no dealer role is granted customer.change_name.
        canChangeName:
            affordable && canEditCustomer && hasPermission({ resource: 'customer', action: 'change_name' }),
        isLiveReadFailed: live.isError,
    };
}
